<template>
    <div class="form-group position-relative">
        <div class="d-flex align-items-center">
            <label v-if="label"
                   :for="id">{{ label }}
                <span class="bottom-link"
                      @click="$emit('label-action')">{{ editLabel }}</span>
            </label>
            <div v-if="info" class="with-tooltip">
                <img class="info-tooltip" src="../../assets/img/question.svg">
                <div class="tooltiptext" v-html="info"></div>
            </div>
        </div>
        <div class="input-wrapper"
             :class="{'form-group-append': append !== undefined}">
            <input :id="id"
                   :type="inputType"
                   :value="value"
                   :name="name"
                   ref="input"
                   :class="{'successInput': showSuccessClass,
                            'errorInput'  : showErrorClass,
                            'input': showInputClass,
                            'blurGrey': greyOnBlur,
                            'hideFont': disabled && !showMessageOnDisabled
                            }"
                       :style="{'width': inputWidth,
                            'min-width': `${minWidth}px`}"
                   :maxlength="maxLength"
                   :disabled="disabled"
                   :autocomplete="autocomplete"
                   :placeholder="placeholder"
                   @keydown="onKeyDown"
                   @input="onInput"
                   @change="onInput"
                   @blur="$emit('blur')"
                   @focus="$emit('focus')">
            <img class="inside"
                 v-if="showSuccessClass"
                 src="../../assets/img/green-check.svg">
            <div class="inside"
                 v-if="showErrorClass">?</div>
            <button class="append secondary-btn btn-center"
                    type="button"
                    ref="append"
                    :disabled="append.disabled"
                    v-if="append"
                    @click="append.callback"
                   :style="{'min-width'  : appendWidth, 'fontSize': appendFontSize}">
                {{ append.text }}
            </button>
        </div>
        <div v-if="errors.has(name)"
             class="error">{{ errors.first(name)}}
        </div>
        <div v-if="!errors.has(name) && maxLength && value && value.length === maxLength"
             class="error">
            Maximum {{ maxLength }} characters
        </div>
    </div>
</template>

<script>
    import { CardNumberMixin } from '../../mixins/CardNumberMixin';
    import { DateMixin } from '../../mixins/DateMixin';
    import { CVVMixin } from '../../mixins/CVVMixin';

    export default {
        name: 'AppInput',
        inject: ['$validator'],
        mixins: [CardNumberMixin, DateMixin, CVVMixin],
        props: {
            id: {
                type: String,
                required: true
            },
            type: {
                type: String,
                required: true
            },
            label: String,
            name: String,
            value: [String, Number],
            disabled: Boolean,
            autocomplete: String,
            placeholder: String,
            error: String,
            append: Object,
            greyOnBlur: Boolean,
            maxLength: Number,
            showCheck: {
                type: Boolean,
                default: true
            },
            readonly: {
                type: Boolean,
                default: false
            },
            minWidth: {
                type: Number,
                default: 120
            },
            editLabel: String,
            info: String,
            showMessageOnDisabled: {
                type: Boolean,
                default: true
            }
        },
        computed: {
            showSuccessClass() {
                return this.showCheck && this.fields[this.name] && this.fields[this.name].valid && !this.disabled &&
                    (this.value && this.value.length ? this.fields[this.name].touched : this.fields[this.name].validated);
            },
            showErrorClass() {
                return this.showCheck && this.fields[this.name] && !this.fields[this.name].valid && this.fields[this.name].validated && !this.disabled;
            },
            showInputClass() {
                return this.fields[this.name] &&
                    (this.value && this.value.length ? this.fields[this.name].touched : this.fields[this.name].validated);
            },
            inputWidth() {
                return this.append && this.append.width ? `width: calc(100% - ${this.append.width}px)` : '100%'
            },
            appendWidth() {
                return this.append && this.append.width   ? `${this.append.width}px`   : '166px';
            },
            appendFontSize() {
                return this.append && this.append.fontSize ? `${this.append.fontSize}px` : '16px';
            },
            inputType() {
                let type;
                if (this.type === 'card' || this.type === 'date' || this.type === 'cvv') {
                    type = 'text';
                }
                else {
                    type = this.type;
                }
                return type;
            }
        },
        $_veeValidate: {
            value () {
                return this.value;
            },
            name () {
                return this.name;
            },
        },
        methods: {
            onInput(event) {
                if (!this.readonly) {
                    this.$emit('input', event.target.value);
                    this.$validator.validate(this.name);
                }
                else {
                    event.preventDefault();
                }
            },
            onChange(event) {
                this.$emit('change', event.target.value);
                this.$validator.validate(this.name);
            },
            onKeyDown(event) {
                if (this.readonly) {
                    event.preventDefault();
                }
            }
        },
        mounted() {
            switch (this.type) {
                case 'card':
                    this.addCreditCardMask(this.$refs.input);
                    break;
                case 'date':
                    this.addDateMask(this.$refs.input);
                    break;
                case 'cvv':
                    this.addCVVMask(this.$refs.input);
                    break;
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "../../assets/colors";

    .input-wrapper {
        width: 100%;
        position: relative;
        display: flex;
        align-items: center;
        @media screen and (max-width: 425px) {
            flex-wrap: wrap;
        }

        .inside {
            position: absolute;
            right: 17px;
            @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
                top: 10px;
                right: 10px;
            }
        }

        div.inside {
            color: $InputError;
            font-size: 18px;
            font-family: Asap;
        }
    }

    .input {
        padding-right: 48px;
        border-radius: 4px;
    }
    .successInput {
        border: 2px solid $Success !important;
    }

    .errorInput {
        border: 2px solid $InputError !important;
    }

    .blurGrey {
        color: $GreyOpacity;
        &:focus {
            color: $Dark;
        }
    }

    .form-group-append {
        display: flex;
        input {
            border-radius: 4px 0 0 4px;
        }
        .append {
            height: 49px;
            cursor: pointer;
            width: 166px;
            border-left: none;
            font-family: Asap-Medium;
            border-radius: 0 4px 4px 0;
            @media screen and (max-width: 568px) {
                width: 170px;
            }
            @media screen and (max-width: 425px) {
                width: 100%;
                border-radius: 0 0 4px 4px;
                border-left: solid 2px $ComplementaryBgTxtDarker;
            }
        }
    }

    .hideFont {
        color: transparent;
    }

    .with-tooltip {
        display: flex;
        justify-content: center;
        align-items: center;
        padding-left: 1px;
    }
</style>


// WEBPACK FOOTER //
// src/components/controls/AppInput.vue